<script setup lang="ts">
import ProfileNav from '@/components/molecules/ProfileNav/ProfileNav.vue'
import UserNewsBell from '@/components/molecules/UserNewsBell/UserNewsBell.vue'
import { useAuthenticationStore } from '@/stores/useAuthenticationStore'
import { useHostNameStore } from '@/stores/useHostNameStore'
import { useAppConfigurationStore } from '@/stores/useAppConfigurationStore'
import { useTabSync } from '@/composables/useTabSync'
import { ref, watch, computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
defineProps<{
    useWhiteLogo?: boolean
}>()

const open = ref(false)
const route = useRoute()
const headerRef = ref(null);

const authStore = useAuthenticationStore()
const hostNameStore = useHostNameStore()
const appConfigStore = useAppConfigurationStore()
const tabSync = useTabSync()
const { isLoggedIn, userFirstName, userLastname } = storeToRefs(authStore)
watch(route, () => (open.value = false))

// Make logoUrl reactive so changes in app configuration are reflected immediately
const logoUrl = computed(() => {
    return appConfigStore.configuration?.logoUrl || '/images/logo/DN_Logo_3Linje_Sand.svg'
})

// Simple version tooltip - reads from window.__APP_VERSION__ injected by main.ts
// @ts-ignore - window.__APP_VERSION__ is injected by main.ts
const versionTooltip = computed(() => {
    const version = (window as any).__APP_VERSION__
    return version ? `Version: ${version}` : ''
})
</script>
<template>
    <header class="header container-fluid" ref="headerRef">
        <div class="header__mobile d-lg-none">


            <RouterLink class="header__logo"
                :to="{ name: appConfigStore.configuration.header.logoLinkTo }">
                <img v-if="!useWhiteLogo" :src="logoUrl"
                    :title="versionTooltip"
                    alt="Danmarks Naturfredningsforening xxx" />
                <img v-else :src="logoUrl"
                    :title="versionTooltip"
                    alt="Danmarks Naturfredningsforening" />
            </RouterLink>
            <div class="header__toggler">
                <button class="burger" :class="{ 'burger--open': open }"
                    type="button" aria-controls="navList" :aria-expanded="open"
                    aria-label="Toggle navigation" @click="open = !open">
                    <span class="burger__label">Menu</span>
                    <span class="burger__label">Luk</span>
                    <span class="burger__lines"></span>
                </button>
            </div>
        </div>
        <div class="header__content" :class="{ 'header__content--open': open }">

            <RouterLink class="header__logo d-none d-lg-block"
                :to="{ name: appConfigStore.configuration.header.logoLinkTo }">
                <img v-if="!useWhiteLogo" :src="logoUrl"
                    :title="versionTooltip"
                    alt="Danmarks Naturfredningsforening" />
                <img v-else :src="logoUrl"
                    :title="versionTooltip"
                    alt="Danmarks Naturfredningsforening" />
            </RouterLink>
            <nav id="navList" class="header__nav" title="Main navigation">
                <ul class="header__list">
                    <li class="header__item"
                        v-if="isLoggedIn && appConfigStore.configuration.header.showHome">
                        <RouterLink class="header__link" :to="{ name: 'home' }">
                            {{ $t('navigation.header.frontpage') }}</RouterLink>
                    </li>
                    <li class="header__item"
                        v-if="isLoggedIn && appConfigStore.configuration.header.showCreateEvent">
                        <RouterLink class="header__link"
                            :to="{ name: 'createEventStep1' }">{{
                                $t('navigation.header.create-event') }}
                        </RouterLink>
                    </li>
                    <li class="header__item" v-if="isLoggedIn">
                        <RouterLink class="header__link"
                            :to="{ name: 'my-events' }">{{
                                $t('navigation.header.my-events') }}
                        </RouterLink>
                    </li>

                    <li class="header__item"
                        v-if="isLoggedIn && appConfigStore.configuration.header.showEvents">
                        <RouterLink class="header__link"
                            :to="{ name: 'events-search' }">Arrangementer
                        </RouterLink>
                    </li>
                    <li class="header__item"
                        v-if="appConfigStore.configuration.header.showHelp">
                        <a :href="appConfigStore.configuration.header.helpLink"
                            target="_blank" class="header__link">Hjælp</a>
                    </li>
                </ul>
            </nav>
            <div v-if="tabSync.activeTabCount.value > 1" class="header__tab-indicator" :title="`${tabSync.activeTabCount.value} aktive faner`">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                </svg>
                <span class="header__tab-count">{{ tabSync.activeTabCount.value }}</span>
            </div>
            <div class="header__actions">
                <UserNewsBell v-if="isLoggedIn" />
                <ProfileNav />
            </div>
        </div>
    </header>
</template>

<style lang="scss">
.header {
    background-color: #ffffff;
   
    
    z-index: 960;
    position: relative;

    &__mobile {
        border-bottom: 1px solid $dn-blue;
        display: flex;
    }

    &__toggler {
        padding-top: 35px;
    }

    &__content {
        @include media-breakpoint-down(lg) {
            @include padding(0 100px 100px 100px);
            display: none;
            background-color: $dn-white;
            position: absolute;
            width: 100%;
            left: 0;
            z-index: 1;
            box-shadow: 0px 20px 20px rgba($dn-black, 0.04);
        }

        @include media-breakpoint-up(lg) {
            border-bottom: 1px solid $dn-blue;
            display: flex;
            flex-direction: row;
            align-items: center;
            height: 100px;
        }
    }

    &__content--open {
        @include media-breakpoint-down(lg) {
            display: block;
        }
    }

    &__logo {
        display: block;
        margin: 15px auto 15px 0;
        // width: 130px;

        @include media-breakpoint-up(lg) {
            margin: 0 auto 0 0;
        }

        img {
            max-height: 75px;
            height: 100%;
            width: 100%;
            //   max-width: 100%;
        }
    }

    &__nav {
        border-bottom: 1px solid $dn-blue-light;
        padding-bottom: 20px;
        margin-bottom: 30px;
        margin-top: 30px;

        @include media-breakpoint-up(lg) {
            border: none;
            padding: 0;
            margin: 0;
            height: 100%;
        }
    }

    &__list {
        list-style-type: none;
        padding: 0;
        margin: 0;

        @include media-breakpoint-up(lg) {
            display: flex;
            flex-direction: row;
            height: 100%;
            align-items: center;
        }
    }

    &__item {
        margin: 10px 30px;
        height: 100%;
        position: relative;
        display: flex;
        align-items: center;
        color: $dn-black;

        @include media-breakpoint-up(lg) {
            margin: 0 30px;
        }
    }

    &__link {
        color: inherit;
        text-decoration: none;
        font-weight: $font-weight-bold;
        font-size: 18px;

        &:hover {
            text-decoration: underline;
        }

        @include media-breakpoint-only(lg) {
            font-size: 18px;
        }
    }

    .router-link-active:not(.header__logo, .profile__link) {
        color: $dn-blue;
        opacity: 0.8;

        &:after {
            content: '';
            display: block;
            position: absolute;
            background-color: $dn-blue;
            width: 26px;
            left: -30px;
            top: calc(50% - 1px);
            height: 3px;

            @include media-breakpoint-up(lg) {
                border-radius: 3px;
                left: 0;
                bottom: -3px;
                top: auto;
                height: 5px;
                width: 100%;
            }
        }
    }

    &--transparent {
        background-color: transparent;

        .header__content {
            border-bottom-color: $dn-white;
        }

        .burger {
            color: $dn-white;

            &__lines {

                &::before,
                &::after {
                    background-color: $dn-white;
                }
            }
        }

        .profile {
            color: $dn-white;
        }

        .header__link {
            @include media-breakpoint-up(lg) {
                color: $dn-white;
            }
        }

        .router-link-active:not(.header__logo) {
            color: $dn-white;

            &::after {
                background-color: $dn-white;
            }
        }

    }

    .router-link-exact-active {
        font-weight: bold;

    }

    &__tab-indicator {
        display: flex;
        align-items: center;
        gap: 6px;
        background-color: #ff6b6b;
        color: white;
        padding: 6px 12px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 600;
        margin: 0 15px;
        cursor: help;
        transition: all 0.2s ease;

        &:hover {
            background-color: #ee5a6f;
            transform: scale(1.05);
        }

        svg {
            flex-shrink: 0;
        }
    }

    &__tab-count {
        min-width: 16px;
        text-align: center;
    }

    &__actions {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: rem(16);
        margin-top: rem(24);

        @include media-breakpoint-up(lg) {
            flex-direction: row;
            align-items: center;
            gap: rem(18);
            margin-top: 0;
            margin-left: 0;
        }
    }

}

.mineture-tidtiltur-dk {
    .router-link-active:not(.header__logo, .profile__link) {
        color: $dn-black;

        &:after {
            background-color: $dn-black;
        }
    }

    .profile__text {
        color: $dn-black;
    }

}

.events-naturensuge-dk {

    .header__link,
    .router-link-active:not(.header__logo, .profile__link) {
        color: $dn-white !important;
        font-size: 21px;
        text-transform: uppercase;

        &:after {
            background-color: $dn-white !important;
        }
    }

    .header {
        .profile__text,
        .profile__login-button {
            color: $dn-white !important;
            text-transform: uppercase;
            font-size: 21px;
            font-family: var(--font-serif);
            text-decoration: none;
        }
    }

    @include media-breakpoint-down(lg) {

        .header__item {
            margin: 0;

            .header__link,
            .router-link-active:not(.header__logo, .profile__link) {
                color: $dn-black !important;

            }
        }

    }
}
</style>
